import { useMemo, Children, cloneElement } from "react";
import { Swiper } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { styled } from "@mui/material/styles";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useScreen } from "hooks";

import Arrows from "../Arrows";
import SliderPagination from "../Pagination";

const StyledSwiper = styled(Swiper)(() => ({
  "& .swiper-wrapper": {
    display: "flex",
    alignItems: "end",
  },
}));

const MIN_SLIDES_FOR_LOOP = 5; // Layout-driven minimum for centeredSlides + auto width to loop without blank gaps
const BULLET_ACTIVE_CLASS = "swiper-pagination-bullet-active";

const Slider = ({ children }) => {
  const { isMobile, isDesktop } = useScreen();
  const slideCount = Children.count(children);
  const shouldDuplicateSlides = slideCount >= 2 && slideCount < MIN_SLIDES_FOR_LOOP;

  const slides = useMemo(() => {
    if (!shouldDuplicateSlides) return children;

    const slidesArray = Children.toArray(children);
    const numberOfCopiesPerSlide = Math.ceil(MIN_SLIDES_FOR_LOOP / slideCount);

    return Array.from({ length: numberOfCopiesPerSlide }).flatMap((_, copyIndex) =>
      slidesArray.map((child, slideIndex) => cloneElement(child, { key: `slide-${slideIndex}-copy-${copyIndex}` })),
    );
  }, [children, shouldDuplicateSlides]);

  const handleSlideChange = (swiper) => {
    if (!shouldDuplicateSlides || !swiper?.pagination?.bullets?.length) return;

    const { bullets } = swiper.pagination;
    const originalIndex = swiper.realIndex % slideCount;
    const isDuplicatePosition = swiper.realIndex >= slideCount;

    if (!isDuplicatePosition) return;

    bullets.forEach((bullet) => bullet.classList.remove(BULLET_ACTIVE_CLASS));

    if (bullets[originalIndex]) bullets[originalIndex].classList.add(BULLET_ACTIVE_CLASS);
  };

  return (
    <div data-testid="category-feature-panel">
      <StyledSwiper
        key={isDesktop ? "desktop" : "mobile"}
        slidesPerView="auto"
        centeredSlides
        loop={slideCount > 1}
        grabCursor
        slideToClickedSlide={isMobile}
        modules={[Navigation, Pagination, Autoplay]}
        onSlideChange={handleSlideChange}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        pagination={{
          el: ".category-pagination",
          clickable: isDesktop,
          type: "bullets",
          renderBullet: (index, className) => {
            if (index < slideCount) return `<span class="${className}" data-index="${index}"></span>`;
            return "";
          },
        }}
        navigation={{
          nextEl: ".category-next-element",
          prevEl: ".category-prev-element",
        }}
      >
        {slides}
        <Arrows />
        <SliderPagination />
      </StyledSwiper>
    </div>
  );
};

export default Slider;
